import React, { useContext } from "react";
import PropTypes from "prop-types";
import "./booking.scss";
import MyInformationsContainer from "app/pages/Booking/MyInformations/MyInformationsContainer";
import { matchPath, Redirect, Route, Switch } from "react-router-dom";
import {
	BRANDS_WITH_BREAK,
	PARTNER_CODE_WITH_BREAK,
	PRODUCTS_VISIBILITY,
	QUOTATION_CODE_STATUS,
	STRATEGY,
} from "app/constants";
import { connect } from "react-redux";
import OauthRoute from "app/pages/.shared/OauthRoute";
import BookingPrivateRoute from "app/pages/.shared/BookingPrivateRoute";
import { dispatchOnQueries } from "app/utils/routerUtils";
import { brandPropTypes, strategyShape } from "app/utils/propTypes";
import {
	AsyncAuthBookingContainer,
	AsyncBookingAsideContainer,
	AsyncBookingSearchContainer,
	AsyncConfirmationContainer,
	AsyncQuotationContainer,
} from "app/routes";
import PaymentContainer from "./Payment/PaymentContainer";
import { isWhiteLabel } from "app/reducers/brandSelector";
import AppGlobalsContext from "app/AppGlobalsContext";
import SmartDPHotelDescriptionContainer from "app/pages/SmartDP/Quote/HotelDescription/SmartDPHotelDescriptionContainer";
import UsefulInformation from "app/pages/FicheProduit/components/UsefulInformation/UsefulInformation";
import Collapse from "rc-collapse";
import { FormattedMessage } from "react-intl";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import CollapsableHolidaySummaryContainer from "app/pages/Booking/HolidaySummary/CollapsableHolidaySummary/CollapsableHolidaySummaryContainer";

const Booking = ({
	location,
	match,
	strategy,
	isWhiteLabel,
	productsVisibility,
	brand,
	ficheProduit,
	quotationStatus,
}) => {
	const currentPathname = location.pathname;

	const { resolution } = useContext(AppGlobalsContext);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const isSearchPage = matchPath(currentPathname, {
		path: "/booking/search",
	});

	const isPaymentPage = matchPath(currentPathname, {
		path: "/booking/payment",
	});

	const isSDPPaymentPage = matchPath(currentPathname, {
		path: "/sdp/booking/payment",
	});

	const isSDPQuotationPage = matchPath(currentPathname, {
		path: "/sdp/booking/quote",
	});

	const isConfirmationPage = matchPath(currentPathname, {
		path: "/booking/confirmation",
	});

	const isSDPConfirmationPage = matchPath(currentPathname, {
		path: "/sdp/booking/confirmation",
	});

	const { partnerCode } = useContext(AppGlobalsContext);

	const shouldBreakPage =
		productsVisibility !== PRODUCTS_VISIBILITY.PREVIEW &&
		(BRANDS_WITH_BREAK.includes(brand) || PARTNER_CODE_WITH_BREAK.includes(partnerCode));

	const informationComponent = isSDPPaymentPage ? (
		<MyInformationsContainer />
	) : (
		<UsefulInformation usefulInformations={ficheProduit.usefulInformations} />
	);

	const accordeonTitleId = isSDPPaymentPage
		? "payment.myinformations.fa.title"
		: "useful.information.title";

	const isSDP = match.url.includes("sdp");

	// for now, /sdp/ urls are always transactionFirst, we may need a flashsale and sdp strategy per partner

	const strategy2 = isSDP ? STRATEGY.TRANSACTION_FIRST : strategy;

	// il faut verifier le quotationStatus pour eviter pb de sticky sur ios mobile
	const showHolidaySummary =
		!isSearchPage &&
		!isConfirmationPage &&
		!isSDPConfirmationPage &&
		isMobile &&
		quotationStatus === QUOTATION_CODE_STATUS.SUCCESS;

	return (
		<React.Fragment>
			<div className="booking">
				<Route
					path={`${match.url}/auth`}
					render={props => {
						if (isWhiteLabel) {
							return <AsyncAuthBookingContainer {...props} />;
						}
						return (
							<Redirect
								to={{
									pathname: "/not-found",
								}}
							/>
						);
					}}
				/>
				<div className="booking__container">
					<div className="booking__main">
						{isSDP && (
							<Route path={[`${match.url}/quote`]}>
								<SmartDPHotelDescriptionContainer />
							</Route>
						)}

						<Switch>
							<Route path={`${match.url}/search`}>
								<AsyncBookingSearchContainer />
							</Route>

							<Route
								path={`${match.url}/quote`}
								render={props => {
									// TODO remettre après coronavirus
									if (shouldBreakPage) {
										return <Redirect to={{ pathname: "/break" }} />;
									}
									dispatchOnQueries(props);
									return (
										<AsyncQuotationContainer {...props} matchUrl={match.url} />
									);
								}}
							/>

							{strategy2 === STRATEGY.OAUTH ? (
								<OauthRoute path={`${match.url}/payment`}>
									<PaymentContainer matchUrl={match.url} />
								</OauthRoute>
							) : (
								<BookingPrivateRoute
									strategy={strategy2}
									authPath="/booking/auth"
									path={`${match.url}/payment`}
								>
									<PaymentContainer matchUrl={match.url} />
								</BookingPrivateRoute>
							)}
							<Route
								path={`${match.url}/confirmation`}
								render={props => {
									return <AsyncConfirmationContainer {...props} />;
								}}
							/>
						</Switch>
					</div>
					<div className="booking__aside">
						<Switch>
							<Route
								path={`${match.url}/quote`}
								render={props => {
									// TODO remettre après coronavirus
									if (shouldBreakPage) {
										return null;
									}
									return <AsyncBookingAsideContainer {...props} />;
								}}
							/>
							<Route
								path={[
									`${match.url}/search`,
									`${match.url}/payment`,
									`${match.url}/confirmation`,
								]}
								render={props => {
									return <AsyncBookingAsideContainer {...props} />;
								}}
							/>
						</Switch>
					</div>
				</div>

				{showHolidaySummary && (
					<div className="booking__holiday-summary">
						<CollapsableHolidaySummaryContainer />
					</div>
				)}
			</div>

			{isPaymentPage && <MyInformationsContainer />}

			{(isSDPQuotationPage || isSDPPaymentPage) &&
				(isMobile ? (
					<Collapse
						expandIcon={({ isActive }) => {
							return (
								<i
									style={{
										transition: "transform .2s ease",
										transform: `rotate(${isActive ? 180 : 0}deg)`,
									}}
									className="icon icon--arrow-down-grey"
								/>
							);
						}}
					>
						<Collapse.Panel
							headerClass="booking-useful-information__header"
							header={
								<div className="sdp-hotel-description__title">
									<FormattedMessage id={accordeonTitleId} />
								</div>
							}
						>
							<div className="booking-useful-information">{informationComponent}</div>
						</Collapse.Panel>
					</Collapse>
				) : (
					<div className="booking-useful-information">{informationComponent}</div>
				))}
		</React.Fragment>
	);
};

Booking.propTypes = {
	strategy: strategyShape,
	brand: brandPropTypes,
	location: PropTypes.object,
	match: PropTypes.object,
	quotationReloadUrl: PropTypes.string,
	ficheProduit: PropTypes.object,
	isWhiteLabel: PropTypes.bool,
	productsVisibility: PropTypes.string,
	quotationStatus: PropTypes.string,
};

const mapStateToProps = state => {
	return {
		ficheProduit: state.ficheProduit,
		strategy: state.partner.strategy,
		isWhiteLabel: isWhiteLabel(state),
		brand: state.brand.code,
		productsVisibility: state.productsVisibility,
		quotationStatus: state.quotation.status,
	};
};

export default connect(mapStateToProps)(Booking);
